import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Bell, 
  FileText, 
  Clock, 
  Users,
  Star,
  TrendingUp,
  Eye,
  ArrowUpRight,
  Utensils,
  BookOpen,
  ClipboardCheck,
  AlertCircle,
  ChevronRight,
  Filter
} from 'lucide-react';
import { motion } from 'framer-motion';
import { sopData } from '../data/sopData';

const UpdateCard = ({ update }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'recipe': return <Utensils className="w-6 h-6 text-blue-600" />;
      case 'manual': return <BookOpen className="w-6 h-6 text-green-600" />;
      case 'checklist': return <ClipboardCheck className="w-6 h-6 text-amber-600" />;
      case 'announcement': return <Bell className="w-6 h-6 text-purple-600" />;
      default: return <FileText className="w-6 h-6 text-gray-600" />;
    }
  };

  const getBgColor = (type) => {
    switch (type) {
      case 'recipe': return 'bg-blue-50';
      case 'manual': return 'bg-green-50';
      case 'checklist': return 'bg-amber-50';
      case 'announcement': return 'bg-purple-50';
      default: return 'bg-gray-50';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all p-4"
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl ${getBgColor(update.type)}`}>
          {getIcon(update.type)}
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-medium text-gray-900">{update.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{update.description}</p>
            </div>
            {update.priority === 'high' && (
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                High Priority
              </span>
            )}
          </div>
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              {update.time}
            </div>
            <Link
              to={update.link}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View Details
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const StatCard = ({ title, value, icon: Icon, trend }) => (
  <div className="bg-white rounded-xl shadow-sm p-6">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <h3 className="text-2xl font-semibold text-gray-900 mt-2">{value}</h3>
      </div>
      <div className={`p-3 rounded-xl ${trend > 0 ? 'bg-green-50' : 'bg-blue-50'}`}>
        <Icon className={`w-6 h-6 ${trend > 0 ? 'text-green-600' : 'text-blue-600'}`} />
      </div>
    </div>
    {trend && (
      <div className="flex items-center gap-2 mt-4">
        <TrendingUp className={`w-4 h-4 ${trend > 0 ? 'text-green-500' : 'text-blue-500'}`} />
        <span className={`text-sm ${trend > 0 ? 'text-green-600' : 'text-blue-600'}`}>
          {Math.abs(trend)}% from last month
        </span>
      </div>
    )}
  </div>
);

const SOPDashboard = () => {
  // Example recent updates data
  const recentUpdates = [
    {
      type: 'announcement',
      title: 'New Product Launch: Signature Pizza',
      description: 'Introducing our new signature pizza line starting next week. Training materials available.',
      time: '2 hours ago',
      priority: 'high',
      link: '/sop/announcements/a1'
    },
    {
      type: 'recipe',
      title: 'Updated Classic Margherita Recipe',
      description: 'Recipe updated with new proportions and cooking instructions.',
      time: '1 day ago',
      link: '/sop/recipes/r1'
    },
    {
      type: 'manual',
      title: 'Kitchen Safety Manual Update',
      description: 'New safety protocols added for equipment handling.',
      time: '2 days ago',
      link: '/sop/manuals/m1'
    },
    {
      type: 'checklist',
      title: 'New Opening Checklist',
      description: 'Updated opening procedures with additional quality checks.',
      time: '3 days ago',
      link: '/sop/checklists/c1'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Active Recipes" 
          value={sopData.stats.totalRecipes}
          icon={Utensils}
          trend={5}
        />
        <StatCard 
          title="Active Manuals" 
          value={sopData.stats.totalManuals}
          icon={BookOpen}
          trend={2}
        />
        <StatCard 
          title="Active Checklists" 
          value={sopData.stats.totalChecklists}
          icon={ClipboardCheck}
          trend={-3}
        />
        <StatCard 
          title="Recent Updates" 
          value={sopData.stats.recentUpdates}
          icon={Bell}
        />
      </div>

      {/* Recent Updates and Important Announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Updates */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Recent Updates</h2>
            <Link 
              to="/sop/announcements" 
              className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4">
            {recentUpdates.map((update, index) => (
              <UpdateCard key={index} update={update} />
            ))}
          </div>
        </div>

        {/* Quick Access and Pending Actions */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Access</h2>
            <div className="space-y-3">
              <Link
                to="/sop/recipes/new"
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">Create New Recipe</span>
                <ArrowUpRight className="w-4 h-4 text-gray-500" />
              </Link>
              <Link
                to="/sop/manuals/new"
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">Add New Manual</span>
                <ArrowUpRight className="w-4 h-4 text-gray-500" />
              </Link>
              <Link
                to="/sop/checklists/new"
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700">Create Checklist</span>
                <ArrowUpRight className="w-4 h-4 text-gray-500" />
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pending Actions</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700">
                    {sopData.stats.pendingAcknowledgments} Pending Acknowledgments
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-red-600" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOPDashboard; 